import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { PROVIDER_LABEL, VideoEmbed } from "../../components/dashboard/resources/VideoEmbed";
import { MarkdownView } from "../../components/dashboard/resources/MarkdownView";
import { ANALYTICS_EVENT, recordEvent } from "../../hooks/useAnalytics";
import { useResourceBySlug, type ResourceCategory } from "../../hooks/useResources";
import styles from "./ResourceDetail.module.css";

const categoryLabel = (c: ResourceCategory): string => {
  switch (c) {
    case "tutorial":
      return "Tutorial";
    case "concept":
      return "Concept";
    case "troubleshooting":
      return "Troubleshooting";
    case "update":
      return "Update";
    case "inspiration":
      return "Inspiration";
  }
};

const categoryDotClass = (c: ResourceCategory): string => {
  switch (c) {
    case "tutorial":
      return styles.dotTutorial ?? "";
    case "concept":
      return styles.dotConcept ?? "";
    case "troubleshooting":
      return styles.dotTroubleshooting ?? "";
    case "update":
      return styles.dotUpdate ?? "";
    case "inspiration":
      return styles.dotInspiration ?? "";
  }
};

const formatDuration = (seconds: number | null): string | null => {
  if (seconds === null || seconds <= 0) return null;
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  if (mm === 0) return `${ss} sec`;
  return `${mm} min ${ss > 0 ? `${ss} sec` : ""}`.trim();
};

const BackArrow = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M10 3.5L5.5 8l4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ResourceDetail = (): JSX.Element => {
  const { slug } = useParams<{ slug: string }>();
  const { status, resource, error } = useResourceBySlug(slug);

  // Record a resource_view event once the slug is known. Fire-and-forget;
  // doesn't gate the page render. Re-fires on slug change so deep-link
  // navigation between resources is captured.
  useEffect(() => {
    if (!slug) return;
    void recordEvent({ type: ANALYTICS_EVENT.resourceView, subject: slug });
  }, [slug]);

  if (status === "loading") {
    return (
      <div className={styles.page}>
        <div className={styles.shellLoading}>Loading…</div>
      </div>
    );
  }

  if (status === "not_found" || !resource) {
    return (
      <div className={styles.page}>
        <Link to="/dashboard/overview" className={styles.backLink}>
          <BackArrow />
          Back to dashboard
        </Link>
        <div className={styles.notFound}>
          <span className={styles.eyebrow}>// 404</span>
          <h1 className={styles.notFoundTitle}>Resource not found</h1>
          <p className={styles.notFoundBody}>
            We couldn&rsquo;t find a resource with slug &ldquo;{slug}&rdquo;. It may have been
            unpublished or removed.
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={styles.page}>
        <Link to="/dashboard/overview" className={styles.backLink}>
          <BackArrow />
          Back to dashboard
        </Link>
        <div className={styles.error}>
          Couldn&rsquo;t load resource{error ? ` — ${error}` : ""}.
        </div>
      </div>
    );
  }

  const duration = formatDuration(resource.durationSeconds);

  return (
    <article className={styles.page}>
      <Link to="/dashboard/overview" className={styles.backLink}>
        <BackArrow />
        Back to dashboard
      </Link>

      <header className={styles.header}>
        <span className={styles.eyebrow}>// RESOURCE</span>
        <h1 className={styles.title}>{resource.title}</h1>
        {resource.description && (
          <p className={styles.description}>{resource.description}</p>
        )}
        <div className={styles.metaRow}>
          <span className={styles.categoryChip}>
            <span className={`${styles.dot} ${categoryDotClass(resource.category)}`} />
            {categoryLabel(resource.category)}
          </span>
          {duration && (
            <span className={styles.metaItem}>
              <span aria-hidden>·</span> {duration}
            </span>
          )}
          {resource.videoUrl && (
            <a
              className={styles.metaLink}
              href={resource.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {resource.videoProvider
                ? `Watch on ${PROVIDER_LABEL[resource.videoProvider]} ↗`
                : "Open original ↗"}
            </a>
          )}
        </div>
      </header>

      {resource.videoProvider && resource.videoId ? (
        <div className={styles.videoWrap}>
          <VideoEmbed
            provider={resource.videoProvider}
            videoId={resource.videoId}
            title={resource.title}
          />
        </div>
      ) : null}

      {resource.bodyMarkdown ? (
        <section className={styles.bodySection} aria-label="Notes">
          <MarkdownView body={resource.bodyMarkdown} />
        </section>
      ) : null}
    </article>
  );
};
